<script setup lang="ts">
import type { components } from '@/api/clarity'
import PrivacyDialog from '@/components/PrivacyDialog.vue'
import TopBar from '@/components/TopBar.vue'
import { Toaster } from '@/components/ui/sonner'
import { dbKey, type DatabaseType } from '@/db'
import { clarityClient, versionMiddleware } from '@/lib/clarity_client'
import { initPlausible } from '@/lib/events'
import { getAuthInstance } from '@/lib/firebase'
import { safeDateTimeFormat } from '@/lib/intl'
import { isAndroid, isIOS } from '@/lib/platform'
import { rollbarError, useRollbar } from '@/lib/rollbar'
import { accountKey, userDisplayName, userKey } from '@/lib/user'
import { useVersionCheck } from '@/lib/vue'
import { postSigninRoute, savePostSigninRoute } from '@/router/signin'
import type { User } from '@firebase/auth'
import { useHead } from '@unhead/vue'
import { useIdle, useLocalStorage, usePreferredLanguages, useWindowFocus } from '@vueuse/core'
import { ConfigProvider } from 'reka-ui'
import {
  computed,
  defineAsyncComponent,
  onBeforeMount,
  onBeforeUnmount,
  onMounted,
  provide,
  ref,
  shallowRef,
  watch,
} from 'vue'
import { useRoute, useRouter } from 'vue-router'
import 'vue-sonner/style.css'

const auth = getAuthInstance()
const user = shallowRef(auth.currentUser)
const route = useRoute()
const router = useRouter()
const rollbar = useRollbar()
const currentVersion = import.meta.env.VITE_APP_VERSION as string | undefined

const TopBarAuthenticated = defineAsyncComponent({
  loader: () => import('@/components/TopBarAuthenticated.vue'),
  loadingComponent: TopBar,
  delay: 0,
})

const database = shallowRef<DatabaseType>()

provide(dbKey, database)
provide(userKey, user)
provide('currentVersion', currentVersion)

// This title template is inherited by all other views/components
useHead({
  titleTemplate: (title?: string) => (!title ? 'ClarityBoss' : `${title} | ClarityBoss`),
  link: [{ rel: 'canonical', href: () => `https://www.clarityboss.com${route.path}` }],
  meta: [{ property: 'og:url', content: () => `https://www.clarityboss.com${route.path}` }],
})

const loggedIn = computed(() => !!user.value)

onBeforeMount(() => initPlausible(loggedIn))

clarityClient.use(versionMiddleware(currentVersion))

const updateAvailable = useVersionCheck(currentVersion)

const languages = usePreferredLanguages()
const formatOptions = computed(() => safeDateTimeFormat(languages.value).resolvedOptions())

const account = shallowRef<components['schemas']['AccountResponseBody']>()
provide(accountKey, account)

watch(
  user,
  async (u) => {
    if (!u) {
      account.value = undefined
      return
    }

    const displayName = userDisplayName(u)
    const locale = formatOptions.value.locale
    const timeZone = formatOptions.value.timeZone

    const { data, error } = await clarityClient.POST('/v1/api/account', {
      body: { displayName, locale, timeZone },
    })

    if (error) {
      rollbar.error('Error updating account', error)
    }
    account.value = data
  },
  { immediate: true },
)

const authCallback = (u: User | null) => {
  user.value = u
  if (u) {
    import('@/db/init').then(
      async ({ configureRxPlugins, initDatabase }) => {
        try {
          configureRxPlugins()
          if (import.meta.hot && import.meta.hot.data.databaseClose) {
            await import.meta.hot.data.databaseClose
            import.meta.hot.data.databaseClose = undefined
          }
          // attempt to avoid some weird race condition we've seen in RxDB in background tabs
          const isBackground = document.visibilityState !== 'visible'
          if (isBackground) {
            console.info('Delaying database initialization due to visibilityState')
            await new Promise((resolve) => setTimeout(resolve, 2000))
          }
          database.value = await initDatabase(u.uid, u.getIdToken.bind(u), currentVersion, rollbar)
          if (isBackground && route.name === 'signin') {
            await router.push(postSigninRoute())
          }
        } catch (err) {
          rollbar.error('Error initializing database', rollbarError(err))
        }
      },
      (err) => {
        rollbar.error('Error loading database module', err)
      },
    )
  } else {
    database.value?.close().catch((err) => {
      rollbar.error('Error closing database', err)
    })
    database.value = undefined
    privacyModeActive.value = false
    if (route.meta.requiresAuth) {
      savePostSigninRoute(route.fullPath)
      router.push({ name: 'signin' }).catch((err) => {
        rollbar.error('Error redirecting to signin after signout', err)
      })
    }
  }
}

onBeforeMount(() => {
  const unsubscribe = auth.onAuthStateChanged(authCallback)

  onBeforeUnmount(() => {
    unsubscribe()
    const closePromise = database.value?.close()
    if (import.meta.hot) {
      import.meta.hot.data.databaseClose = closePromise
    }
  })
})

// On mobile platforms, we don't want to automatically enter privacy mode, since it is a private device by default.
// For desktop, enable it by default, but allow the user to disable it.
const privacyModeEnabled = useLocalStorage('privacyModeEnabled', !isIOS() && !isAndroid(), {
  listenToStorageChanges: true,
})

const privacyModeActive = ref(false)

function hasUser(user: User | null): user is User {
  return user !== null
}

function setupPrivacyMode() {
  // we wait 3 minutes to enter privacy mode if the window is focused, or 1 minute if the window is not focused.
  const isWindowFocused = useWindowFocus()
  const idleOpts = { listenForVisibilityChange: false }
  const { idle: idleFocused } = useIdle(180_000, idleOpts)
  const { idle: idleUnfocused } = useIdle(60_000, idleOpts)

  watch([isWindowFocused, idleFocused, idleUnfocused], ([focus, idleFocus, idleUnfocus]) => {
    // only enable privacy mode automatic switching if enabled and a user is logged in, and the route is marked private
    if (!privacyModeEnabled.value || !user.value || route.meta.privacy !== true) {
      return
    }
    if ((focus && idleFocus) || (!focus && idleUnfocus)) {
      privacyModeActive.value = true
    }
  })
}

onMounted(setupPrivacyMode)
</script>

<template>
  <ConfigProvider :scroll-body="false" :locale="formatOptions.locale">
    <TopBarAuthenticated
      v-if="hasUser(user)"
      v-model:privacy-mode-active="privacyModeActive"
      v-model:privacy-mode-enabled="privacyModeEnabled"
      :user="user"
    />
    <TopBar v-else />
    <div>
      <router-view />
    </div>
    <Toaster />
    <PrivacyDialog v-model="privacyModeActive" :update-available="updateAvailable" />
  </ConfigProvider>
</template>
